import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, Users, ArrowRight, Sparkles, Heart, HelpCircle, Timer, CheckSquare } from "lucide-react";

const faqData = [
  {
    question: "What happened to Mailbox?",
    answer: "Mailbox was discontinued on February 26, 2016 after Dropbox decided to focus on its core storage business. CSW offers free productivity tools as an alternative approach to managing your workflow."
  },
  {
    question: "Is CSW related to email like Mailbox?",
    answer: "CSW isn't an email app, but it helps you be more productive with task management, timers, and focus sessions—complementing any email workflow."
  },
  {
    question: "What features does CSW offer?",
    answer: "CSW offers 24/7 virtual study rooms, Pomodoro timers, Task Management with to-do lists and Kanban boards, and reminders—all free."
  },
  {
    question: "Can CSW help with productivity like Mailbox did?",
    answer: "Yes! While Mailbox helped triage email, CSW helps you execute on tasks with focus sessions, accountability partners, and productivity tools."
  },
  {
    question: "Does CSW have task management?",
    answer: "Yes, CSW has comprehensive task management including to-do lists, Kanban boards, and reminders to keep you organized."
  },
  {
    question: "Is CSW free?",
    answer: "Yes, CSW is completely free with unlimited access to all features—no subscription required."
  }
];

const Mailbox = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Mailbox Alternative - Free Productivity Tools"
        description="Looking for a Mailbox alternative? Mailbox shut down in 2016 but CSW offers free productivity tools with Task Management, Study Timer, and virtual focus sessions."
        keywords="mailbox alternative, mailbox app, dropbox mailbox, email productivity, task management, inbox zero"
        canonicalUrl="/mailbox/"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Mailbox Alternative - Free Productivity Tools | CSW",
        "description": "Looking for a Mailbox alternative? CSW offers free productivity tools.",
        "url": "https://csw.live/mailbox/",
        "inLanguage": "en",
        "isPartOf": { "@type": "WebSite", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
        "about": { "@type": "Thing", "name": "Mailbox Alternative" },
        "keywords": "mailbox alternative, mailbox app, dropbox mailbox, productivity tools"
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CSW - Mailbox Alternative",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "1000" },
        "featureList": ["Task Management", "Pomodoro Timer", "Focus Sessions", "Virtual Study Rooms", "Reminders"],
        "publisher": { "@type": "Organization", "name": "CSW", "url": "https://csw.live" }
      })}} />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } }))
      })}} />
      
      <Header />

      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        
        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-gradient">Mailbox</span>
            </h1>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Mailbox Alternative</h1>
            <h2 className="font-display text-2xl sm:text-3xl text-muted-foreground">Free Productivity Tools <span className="text-gradient">24/7</span></h2>
            
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              When Dropbox shut down Mailbox in 2016, the "inbox zero" revolution lost its flagship app. CSW helps you stay productive with free task management and focus tools. <Link to="/study-with-me/" className="text-primary hover:underline">Join our community</Link>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 px-8 py-6 text-lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span>Free Forever</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-8 bg-card/50 border-primary/20">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <div className="space-y-4">
                <h2 className="text-3xl font-bold">Mailbox Has <span className="text-gradient">Shut Down</span></h2>
                <p className="text-muted-foreground text-lg">
                  Mailbox—the innovative email app that introduced swipe gestures and email snoozing—was discontinued on February 26, 2016. Dropbox acquired it for ~$100 million but couldn't make it fit their strategy. <Link to="/mailbox/history/" className="text-primary hover:underline font-medium">Read the full Mailbox story →</Link>
                </p>
                <p className="text-muted-foreground text-lg">
                  While CSW isn't an email app, it helps you be productive with <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link>, <Link to="/study-timer/" className="text-primary hover:underline">Focus Timer</Link>, and <Link to="/group-study/" className="text-primary hover:underline">accountability sessions</Link>.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">Task <span className="text-gradient">Management</span></h2>
              <p className="text-muted-foreground text-lg">
                Mailbox treated email like a to-do list. CSW has <Link to="/task-management/" className="text-primary hover:underline">actual task management</Link> with Kanban boards, to-do lists, and <Link to="/reminders/" className="text-primary hover:underline">reminders</Link>.
              </p>
            </div>
            <Card className="p-8 bg-card/50">
              <div className="aspect-video bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 bg-primary/10 border-primary/20">
              <div className="aspect-video bg-muted/50 rounded-xl flex items-center justify-center">
                <Users className="w-16 h-16 text-muted-foreground/50" />
              </div>
            </Card>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold">Focus <span className="text-gradient">Sessions</span></h2>
              <p className="text-muted-foreground text-lg">
                Join <Link to="/study-stream/" className="text-primary hover:underline">virtual focus sessions</Link> to get work done with accountability partners. It's like "inbox zero" but for your actual tasks.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Frequently Asked <span className="text-gradient">Questions</span></h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg border px-6">
                  <AccordionTrigger className="text-left font-medium py-4">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
        <div className="container relative z-10 mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to <span className="text-gradient">Get Started?</span></h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands using CSW to stay productive and focused.
          </p>
          <Button size="lg" asChild>
            <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
              Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Mailbox;
